import { WS_URL } from '@/config';
import prayerTimeService from './prayerTimeService';
import scheduleService from './scheduleService';
import musicScheduleService from './musicScheduleService';
import specialDayService from './specialDayService';
import deviceService from './deviceService';
import { localStateStore } from '@/utils/localStateStore';

type MessageHandler = (message: any) => void;

class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private messageQueue: any[] = [];
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map();
  private reconnectTimeout: number = 1000;
  private maxReconnectTimeout: number = 30000;
  private shouldReconnect: boolean = true;
  private wasConnected: boolean = false;
  private isAlreadyConnected = false;
  private authRetryTimerId: number | null = null;
  private emergencyScheduleTimerId: number | null = null;
  private reconnectTimerId: number | null = null;
  private activePlaylistResyncTimerId: number | null = null;
  private isDeviceResetting = false;
  private acknowledgedPlaylistAssignments = new Set<string>();
  private readonly maxQueuedMessages = 200;
  private readonly debugMessages = import.meta.env.DEV || import.meta.env.VITE_WS_DEBUG === 'true';

  private constructor() {
    this.bindNetworkEvents();

    // Auth mesajı handler'ı
    this.addMessageHandler('auth', (message) => {
      
      if (message.success) {
        // Auth başarılı olduğunda, yeniden deneme zamanlayıcısını temizle
        this.clearAuthRetryTimer();
        this.wasConnected = true;
        
        // Sadece ilk bağlantıda veya yeniden bağlanıldığında bildirim göster
        if (!localStateStore.getString('initialConnectionDone')) {
          localStateStore.setString('initialConnectionDone', 'true');
        }
        
        // Auth mesajında gelen prayer settings'i kaydet
        if (message.prayerSettings && message.prayerSettings.settings) {
          prayerTimeService.savePrayerSettings(message.prayerSettings.settings);
        }

        const authMusicSchedule = message.device?.musicSchedule;
        if (authMusicSchedule?.enabled) {
          musicScheduleService.setMusicSchedule(authMusicSchedule);
        } else {
          musicScheduleService.deleteMusicSchedule();
        }

        // Auth mesajında gelen volume değerini sadece localStorage'da değer yoksa uygula
        // Böylece kullanıcının local değişikliği korunur
        if (message.device?.volume !== undefined && !localStateStore.getString('playerVolume')) {
          const serverVolume = message.device.volume;
          localStateStore.setString('playerVolume', serverVolume.toString());
          window.dispatchEvent(new CustomEvent('remoteSetVolume', { detail: { volume: serverVolume } }));
          console.log('[WebSocket] Sunucudan ilk volume yüklendi:', serverVolume);
        }

        // Auth mesajında gelen emergencyStopped durumunu kontrol et
        if (message.device?.emergencyStopped) {
          console.log('[WebSocket] Sunucudan acil durum durumu alındı: DURDURULMUŞ');
          localStateStore.setString('emergencyStopped', 'true');
          window.dispatchEvent(new CustomEvent('emergencyStop', { detail: { reason: 'Sunucu durumu' } }));
        } else {
          // Acil durum aktif değilse temizle
          localStateStore.remove('emergencyStopped');
          localStateStore.remove('emergencyReason');
        }

        // Emergency schedule bilgisi varsa kaydet ve timer başlat
        if (message.device?.emergencySchedule?.enabled) {
          const schedule = message.device.emergencySchedule;
          console.log('[WebSocket] Emergency schedule alındı:', schedule);
          localStateStore.setJson('emergencySchedule', schedule);
          this.startEmergencyScheduleChecker();
        } else {
          localStateStore.remove('emergencySchedule');
          this.stopEmergencyScheduleChecker();
        }

        // Auth başarılı olduğunda aktif playlist ve anonsları sorgula
        setTimeout(() => {
          this.requestActivePlaylist();
          this.requestDeviceAnnouncements();
          this.requestPrayerTimes();
          this.requestUiSettings();
          // Player'a bağlantı kurulduğunu bildir - mevcut durumu göndermesi için
          window.dispatchEvent(new CustomEvent('websocketConnected'));
        }, 500); // 500ms bekle ki auth işlemi tam olarak tamamlansın
      } else {
        // Token doğrulama başarısız oldu
        console.warn('Token doğrulama başarısız:', message);
        
        if (message.errorCode === 'device_deleted') {
          void this.resetDeletedDeviceData();
          return;
        }
        
        // Normal auth hata durumu için yeniden deneme zamanlayıcısını başlat
        this.startAuthRetryTimer();
      }
    });

    // Error mesajlarını işle
    this.addMessageHandler('error', (message) => {
      console.error('Sunucudan hata mesajı alındı:', message);
    });

    // UI settings mesajı handler'ı
    this.addMessageHandler('uiSettings', (message) => {
      const settings = message.settings || message.data?.settings || message.data || null;
      if (!settings) {
        return;
      }

      localStateStore.setJson('uiSettings', settings);
      window.dispatchEvent(new CustomEvent('uiSettingsUpdated', {
        detail: { settings }
      }));
    });

    // Playlist mesajı handler'ı
    this.addMessageHandler('playlist', (message) => {
      if (message.data) {
        // ⚠️ ÖNEMLİ: Schedule aktif mi kontrol et (Desktop gibi)
        const activeSchedule = this.isSchedulePlaybackActive();

        const playlistPayload = this.attachDeliveryMetadata(message.data, message);

        if (activeSchedule) {
          // Schedule aktif - normal playlist sadece fallback olarak saklanir.
          this.storeFallbackPlaylist(playlistPayload);
        } else {
          // Schedule yok - normal akış

          // Playlist'i BOTH currentPlaylist VE fallbackPlaylist'e kaydet
          localStateStore.setJson('currentPlaylist', playlistPayload);
          this.storePlaylistSignature(playlistPayload);
          this.markPendingPlaylistAutoplay(playlistPayload);

          // AYRICA fallback olarak da kaydet - schedule geldiğinde dönmek için
          this.storeFallbackPlaylist(playlistPayload);

          // Custom event dispatch et - Player dinleyecek
          const playlistEvent = new CustomEvent('playlistReceived', {
            detail: playlistPayload
          });
          window.dispatchEvent(playlistEvent);
        }
      }

      this.sendPlaylistAck(
        message,
        message.data ? 'received' : 'error',
        message.data ? {} : { error: 'playlist_payload_missing' }
      );

    });

    // Anons mesajı handler'ı
    this.addMessageHandler('announcement', (message) => {
      
      // Eğer checkType: "device_announcements" ise, announcements array'ini kaydet
      if (message.data?.checkType === 'device_announcements' && message.data?.announcements) {
        
        const serverAnnouncements = message.data.announcements;
        const normalizedAnnouncements = this.replaceStoredAnnouncements(serverAnnouncements);
        
        // Tam senkronizasyon eventi fırlat
        const syncEvent = new CustomEvent('announcementsFullSync', {
          detail: {
            announcements: normalizedAnnouncements,
            removed: [],
            added: normalizedAnnouncements
          }
        });
        window.dispatchEvent(syncEvent);
        
        return;
      }
      
      // Normal tek anons mesajı
      if (message.data) {
        const { announcement: normalizedAnnouncement, allAnnouncements: updatedAnnouncements } =
          this.upsertStoredAnnouncement(message.data);
        
        // Anons geldi eventi fırlat
        const announcementEvent = new CustomEvent('announcementReceived', {
          detail: {
            announcement: normalizedAnnouncement,
            allAnnouncements: updatedAnnouncements
          }
        });
        window.dispatchEvent(announcementEvent);
      }
    });

    // Anons güncellemesi handler'ı (announcement-update)
    this.addMessageHandler('announcement-update', (message) => {
      
      if (message.data || message) {
        const announcementData = message.data || message;
        
        const { announcement: normalizedAnnouncement, allAnnouncements: updatedAnnouncements } =
          this.upsertStoredAnnouncement(announcementData);
        
        // Anons güncellemesi eventi fırlat
        const announcementUpdateEvent = new CustomEvent('announcementUpdated', {
          detail: {
            announcement: normalizedAnnouncement,
            allAnnouncements: updatedAnnouncements
          }
        });
        window.dispatchEvent(announcementUpdateEvent);
      }
    });

    // Anons güncellemesi handler'ı (announcement_update - underscore versiyonu)
    this.addMessageHandler('announcement_update', (message) => {
      
      if (message.data || message) {
        const announcementData = message.data || message;
        
        const { announcement: normalizedAnnouncement, allAnnouncements: updatedAnnouncements } =
          this.upsertStoredAnnouncement(announcementData);
        
        // Anons güncellemesi eventi fırlat
        const announcementUpdateEvent = new CustomEvent('announcementUpdated', {
          detail: {
            announcement: normalizedAnnouncement,
            allAnnouncements: updatedAnnouncements
          }
        });
        window.dispatchEvent(announcementUpdateEvent);
      }
    });

    // activePlaylist mesajı handler'ı
    this.addMessageHandler('activePlaylist', (message) => {
      const backendSchedules = Array.isArray(message.data?.allSchedules)
        ? message.data.allSchedules
        : null;
      const backendHasActiveSchedule = backendSchedules
        ? scheduleService.hasActiveSchedule(backendSchedules)
        : false;

      if (backendSchedules) {
        void scheduleService.replaceSchedulesFromBackend(backendSchedules);
      }

      if (message.data && message.data.playlist) {
        const serverPlaylist = message.data.playlist;
        const schedulePlaybackActive = this.isSchedulePlaybackActive() || backendHasActiveSchedule;

        if (schedulePlaybackActive) {
          if (message.data.fallbackPlaylist) {
            this.storeFallbackPlaylist(message.data.fallbackPlaylist);
          } else if (message.data.playlistType === 'normal' || !serverPlaylist._id?.startsWith?.('scheduled_')) {
            this.storeFallbackPlaylist(serverPlaylist);
          }
          return;
        }

        const localPlaylist = localStateStore.getString('currentPlaylist');
        
        let shouldUpdatePlaylist = true;
        
        if (localPlaylist) {
          try {
            const parsedLocalPlaylist = JSON.parse(localPlaylist);
            
            // Playlist ID'leri karşılaştır
            if (parsedLocalPlaylist._id === serverPlaylist._id) {
              // Aynı playlist, şarkı sayısını kontrol et
              const localSongCount = parsedLocalPlaylist.songs?.length || 0;
              const serverSongCount = serverPlaylist.songs?.length || 0;
              
              if (localSongCount === serverSongCount) {
                shouldUpdatePlaylist = false;
              } else {
              }
            } else {
            }
          } catch (error) {
            console.error('Lokal playlist parse hatası:', error);
          }
        }
        
        shouldUpdatePlaylist = this.shouldApplyPlaylist(serverPlaylist);

        if (shouldUpdatePlaylist) {
          // Sunucudaki playlist'i kullan
          localStateStore.setJson('currentPlaylist', serverPlaylist);
          this.storePlaylistSignature(serverPlaylist);

          // Backend'den fallbackPlaylist geliyorsa onu kaydet (schedule varsa)
          if (message.data.fallbackPlaylist) {
            this.storeFallbackPlaylist(message.data.fallbackPlaylist);
            console.log('[WebSocket] fallbackPlaylist backend\'den alındı ve kaydedildi');
          } else if (message.data.playlistType === 'normal') {
            // Normal playlist ise, kendisini fallback olarak kaydet
            this.storeFallbackPlaylist(serverPlaylist);
            console.log('[WebSocket] Normal playlist fallback olarak da kaydedildi');
          }

          // Playlist güncellemesi eventi fırlat
          const playlistEvent = new CustomEvent('playlistReceived', {
            detail: serverPlaylist
          });
          window.dispatchEvent(playlistEvent);

        }
      } else if (message.data && message.data.playlist === null) {
        if (this.isSchedulePlaybackActive() || backendHasActiveSchedule) {
          return;
        }

        // Sunucuda playlist yok
        
        // Sadece daha önce playlist varsa playlistRemoved eventi fırlat
        const hadPlaylistBefore = localStateStore.getString('currentPlaylist') !== null;
        
        if (hadPlaylistBefore) {

          // Lokaldeki playlist state'ini temizle. Signature/fallback kalırsa sonraki sync'te eski içerik dönebilir.
          this.clearPlaylistStorage();

          // Playlist silindi eventi fırlat
          const emptyPlaylistEvent = new CustomEvent('playlistRemoved');
          window.dispatchEvent(emptyPlaylistEvent);
        } else {
        }
      }

    });

    // Anons listesi handler'ı
    this.addMessageHandler('announcements-list', (message) => {
      
      if (message.data || message) {
        const announcementsData = Array.isArray(message.data)
          ? message.data
          : Array.isArray(message.data?.announcements)
            ? message.data.announcements
            : Array.isArray(message.announcements)
              ? message.announcements
              : Array.isArray(message)
                ? message
                : [];
        
        // Tüm anons listesini güncelle
        const normalizedAnnouncements = this.replaceStoredAnnouncements(announcementsData);
        
        // Anons listesi eventi fırlat
        const announcementsListEvent = new CustomEvent('announcementsListReceived', {
          detail: {
            allAnnouncements: normalizedAnnouncements
          }
        });
        window.dispatchEvent(announcementsListEvent);
      }
    });

    // device_announcements handler'ı - Tam senkronizasyon için
    this.addMessageHandler('device_announcements', (message) => {
      
      // message.announcements array'ini direkt al (checkType görmezden gel)
      const serverAnnouncements = Array.isArray(message.announcements) ? message.announcements : [];
      
      const localAnnouncements = this.readStoredAnnouncements();
      
      // Sunucudaki anons ID'lerini topla
      const serverAnnouncementIds = new Set(
        serverAnnouncements.map((ann: any) => ann._id || ann.id).filter(Boolean)
      );
      
      // Lokaldeki anons ID'lerini topla
      const localAnnouncementIds = new Set(
        localAnnouncements.map((ann: any) => ann._id || ann.id).filter(Boolean)
      );
      
      // Karşılaştırma yap
      let needsUpdate = this.createAnnouncementsSignature(serverAnnouncements) !==
        this.createAnnouncementsSignature(localAnnouncements);
      
      // 1. Sunucuda olmayan lokaldeki anonsları tespit et
      const removedAnnouncements = localAnnouncements.filter(
        (ann: any) => !serverAnnouncementIds.has(ann._id || ann.id)
      );
      
      if (removedAnnouncements.length > 0) {
        needsUpdate = true;
      }
      
      // 2. Lokalde olmayan sunucudaki anonsları tespit et
      const newAnnouncements = serverAnnouncements.filter(
        (ann: any) => !localAnnouncementIds.has(ann._id || ann.id)
      );
      
      if (newAnnouncements.length > 0) {
        needsUpdate = true;
      }
      
      // 3. Sayı kontrolü yap
      if (serverAnnouncements.length !== localAnnouncements.length) {
        needsUpdate = true;
      }
      
      if (needsUpdate) {
        // Sunucudaki anonsları normalize et ve kaydet
        const normalizedAnnouncements = this.replaceStoredAnnouncements(serverAnnouncements);
        
        
        // Tam senkronizasyon eventi fırlat
        const syncEvent = new CustomEvent('announcementsFullSync', {
          detail: {
            announcements: normalizedAnnouncements,
            removed: removedAnnouncements,
            added: newAnnouncements
          }
        });
        window.dispatchEvent(syncEvent);
      } else {
      }
    });

    // Command mesajı handler'ı
    this.addMessageHandler('command', (message) => {
      // Backend iki farklı format gönderebilir:
      // 1. { type: 'command', data: { command: '...', ... } }
      // 2. { type: 'command', command: '...', action: '...', ... }
      const command = message.data?.command || message.command;
      const action = message.data?.action || message.action;

      if (!command) {
        console.warn('[WebSocket] Command mesajında command alanı bulunamadı:', message);
        return;
      }

      console.log('[WebSocket] Command alındı:', command, 'Action:', action);
      const ackCommand = (success = true, extra: Record<string, any> = {}) => {
        this.sendCommandAck(message, command, success, extra);
      };

      // music-schedule komutu - Planlı Çalma Saatleri (Desktop gibi)
      if (command === 'music-schedule') {
        const schedule = message.data?.schedule || message.schedule;

        if (action === 'set' && schedule) {
          console.log('[WebSocket] Music schedule set ediliyor:', schedule);
          musicScheduleService.setMusicSchedule(schedule);
          ackCommand(true);
        } else if (action === 'delete') {
          console.log('[WebSocket] Music schedule siliniyor');
          musicScheduleService.deleteMusicSchedule();
          ackCommand(true);
        } else {
          ackCommand(false, { error: 'invalid_music_schedule_command' });
        }
        return;
      }

      // special-day-playlist komutu - Özel Gün Playlist (Ramazan vb.)
      if (command === 'special-day-playlist') {
        const configFromData = message.data?.config || message.config || {};
        const specialDayFromData = message.data?.specialDay || message.specialDay || {};
        const playlist = message.data?.playlist || message.playlist || specialDayFromData.playlist || configFromData.playlist;

        const config = {
          ...configFromData,
          ...specialDayFromData,
          enabled: message.data?.enabled ?? message.enabled ?? specialDayFromData.enabled ?? configFromData.enabled ?? true,
          specialDayType: message.data?.specialDayType || message.specialDayType || specialDayFromData.specialDayType || specialDayFromData.dayType || configFromData.specialDayType || configFromData.dayType || 'ramadan',
          beforeEzanHours: message.data?.beforeEzanHours ?? message.beforeEzanHours ?? specialDayFromData.beforeEzanHours ?? specialDayFromData.beforeHours ?? configFromData.beforeEzanHours ?? configFromData.beforeHours,
          afterEzanMinutes: message.data?.afterEzanMinutes ?? message.afterEzanMinutes ?? specialDayFromData.afterEzanMinutes ?? specialDayFromData.afterMinutes ?? configFromData.afterEzanMinutes ?? configFromData.afterMinutes,
          startDate: message.data?.startDate || message.startDate || specialDayFromData.startDate || configFromData.startDate || null,
          endDate: message.data?.endDate || message.endDate || specialDayFromData.endDate || configFromData.endDate || null,
          playlist
        };

        if (action === 'delete' || config.enabled === false) {
          console.log('[WebSocket] Special day playlist siliniyor');
          specialDayService.deleteSpecialDayConfig();
        } else {
          console.log('[WebSocket] Special day playlist set ediliyor:', config);
          specialDayService.setSpecialDayConfig(config);
        }
        ackCommand(true);
        return;
      }

      // restart komutu - web player'ı yeniden başlat
      if (command === 'restart') {

        // Restart event'ini fırlat
        const restartEvent = new CustomEvent('restartReceived', {
          detail: message
        });
        window.dispatchEvent(restartEvent);
        ackCommand(true);

        // 1 saniye sonra sayfayı yenile
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }

      // deleteAnnouncement komutu - anons silme
      const announcementId = message.data?.announcementId || message.announcementId;
      if (command === 'deleteAnnouncement' && announcementId) {

        const updatedAnnouncements = this.removeStoredAnnouncement(announcementId);

        // Anons silindi eventi fırlat - playlist yeniden oluşturulsun
        const announcementDeletedEvent = new CustomEvent('announcementDeleted', {
          detail: {
            deletedId: announcementId,
            allAnnouncements: updatedAnnouncements
          }
        });
        window.dispatchEvent(announcementDeletedEvent);
        ackCommand(true);
        return;
      }

      if (command === 'deleteAnnouncement') {
        ackCommand(false, { error: 'announcement_id_missing' });
        return;
      }

      // getCurrentStatus komutu - Manager bağlandığında mevcut durumu iste
      if (command === 'getCurrentStatus') {
        console.log('[WebSocket] getCurrentStatus komutu alındı, playback durumu gönderilecek');
        window.dispatchEvent(new CustomEvent('requestPlaybackStatus'));
        ackCommand(true);
        return;
      }

      // play komutu - Manager'dan oynat
      if (command === 'play') {
        console.log('[WebSocket] Uzaktan oynat komutu alındı');
        // Manager play yaptığında remotePaused flag'ini temizle
        localStateStore.remove('remotePaused');
        window.dispatchEvent(new CustomEvent('remotePlay'));
        ackCommand(true);
        return;
      }

      // pause komutu - Manager'dan duraklat
      if (command === 'pause') {
        console.log('[WebSocket] Uzaktan duraklat komutu alındı');
        // Manager pause yaptığında flag kaydet - browser yenilense bile hatırlansın
        localStateStore.setString('remotePaused', 'true');
        window.dispatchEvent(new CustomEvent('remotePause'));
        ackCommand(true);
        return;
      }

      // setVolume komutu - Manager'dan ses seviyesi değiştir
      if (command === 'setVolume') {
        const volume = message.data?.volume ?? message.volume;
        if (typeof volume === 'number' && volume >= 0 && volume <= 100) {
          console.log('[WebSocket] Uzaktan ses seviyesi komutu alındı:', volume);
          window.dispatchEvent(new CustomEvent('remoteSetVolume', { detail: { volume } }));
          ackCommand(true);
        } else {
          ackCommand(false, { error: 'invalid_volume' });
        }
        return;
      }

      // emergency-stop komutu - Acil durdurma
      if (command === 'emergency-stop') {
        const reason = message.data?.reason ?? message.reason ?? 'Acil durum';
        const schedule = message.data?.schedule ?? message.schedule ?? null;

        // Schedule varsa ve başlangıç zamanı gelecekteyse → hemen durdurma, timer başlat
        if (schedule?.enabled && schedule.startDate) {
          const now = new Date();
          const startDate = new Date(schedule.startDate);
          if (startDate > now) {
            console.log('[WebSocket] Zamanlı acil durdur alındı, başlangıç:', schedule.startDate);
            localStateStore.setJson('emergencySchedule', schedule);
            this.startEmergencyScheduleChecker();
            ackCommand(true, { scheduled: true });
            return;
          }
        }

        console.log('[WebSocket] Acil durdurma komutu alındı:', reason);
        localStateStore.setString('emergencyStopped', 'true');
        localStateStore.setString('emergencyReason', reason);
        window.dispatchEvent(new CustomEvent('emergencyStop', { detail: { reason } }));
        ackCommand(true);
        return;
      }

      // emergency-reset komutu - Acil durumu sıfırla
      if (command === 'emergency-reset') {
        console.log('[WebSocket] Acil durum sıfırlama komutu alındı');
        localStateStore.remove('emergencyStopped');
        localStateStore.remove('emergencyReason');
        window.dispatchEvent(new CustomEvent('emergencyReset'));
        ackCommand(true);
        return;
      }

      // Diğer command türleri için genel event
      const commandEvent = new CustomEvent('commandReceived', {
        detail: {
          command: command,
          data: message.data || message
        }
      });
      window.dispatchEvent(commandEvent);
      ackCommand(true, { generic: true });
    });

    // Prayer time mesajı handler'ı
    this.addMessageHandler('prayerTime', (message) => {
      
      // Desktop formatı: message direkt olarak veriyi içeriyor
      prayerTimeService.savePrayerTimes(message);
    });

    // All prayer times mesajı handler'ı
    this.addMessageHandler('allPrayerTimes', (message) => {

      // Desktop formatı: message direkt olarak veriyi içeriyor
      prayerTimeService.savePrayerTimes(message);
    });

    // Haftalık namaz vakti dizisi handler'ı - sekme bağlı kaldığı sürece
    // gün değiştikçe doğru güne kendi kendine geçebilsin diye
    this.addMessageHandler('weeklyPrayerTimes', (message) => {
      prayerTimeService.saveWeeklyPrayerTimes(message);
    });

    // Prayer settings mesajı handler'ı
    this.addMessageHandler('prayerSettings', (message) => {

      // Desktop formatı: message.settings içinde veri geliyor
      const settings = message.settings || message.data || message;
      prayerTimeService.savePrayerSettings(settings);
    });

    // Prayer settings update mesajı handler'ı (cihaz bazlı ezan ayarı değişikliği)
    this.addMessageHandler('prayerSettingsUpdate', (message) => {
      console.log('[WebSocket] prayerSettingsUpdate mesajı alındı:', message);

      try {
        // Backend'den gelen format: { type: 'prayerSettingsUpdate', settings: { enabled, pauseDuringPrayer, pauseDuration } }
        const settings = message.settings || message.data || message;
        console.log('[WebSocket] prayerTimeService.savePrayerSettings çağrılıyor:', settings);
        prayerTimeService.savePrayerSettings(settings);
        console.log('[WebSocket] prayerTimeService.savePrayerSettings tamamlandı');
      } catch (error) {
        console.error('[WebSocket] prayerSettingsUpdate işlenirken hata:', error);
      }
    });
    
    // Certificate mesajı handler'ı
    this.addMessageHandler('certificate', (message) => {
      console.log('[WebSocket] Certificate mesajı alındı:', message);

      // Download action - sertifikayı indir/göster
      if (message.action === 'download' && message.data?.certificateUrl) {
        const { certificateUrl, certificateFileName, title } = message.data;
        console.log('[WebSocket] Sertifika alındı:', certificateUrl);

        // Backend'e alındı onayı gönder
        this.sendMessage({
          type: 'certificate',
          action: 'downloaded'
        });

        // İndir butonu için certificateReceived event'i dispatch et (yeni sekmede açmadan)
        window.dispatchEvent(new CustomEvent('certificateReceived', {
          detail: { data: { certificateUrl, certificateFileName, title } }
        }));

        return;
      }

      // Certificate event'ini window'a dispatch et
      const certificateEvent = new CustomEvent('certificateReceived', {
        detail: message
      });
      window.dispatchEvent(certificateEvent);

      // Certificate bilgisini localStorage'a kaydet (ihtiyaç varsa)
      if (message.certificate) {
        localStateStore.setJson('deviceCertificate', message.certificate);
      }
    });
    
    // Delete mesajı handler'ı
    this.addMessageHandler('delete', (message) => {

      if (message.type === 'delete' && (message.target || message.entityType)) {
        const target = String(message.target || message.entityType).toLowerCase();
        const targetId = message.targetId || message.entityId || message.id;

        if (target === 'device') {
          void this.resetDeletedDeviceData();
          return;
        }

        // Delete event'ini window'a dispatch et
        const deleteEvent = new CustomEvent('deleteReceived', {
          detail: {
            target,
            targetId,
            data: message
          }
        });
        window.dispatchEvent(deleteEvent);
      }
    });

    // ========================================
    // SCHEDULE MESAJ HANDLER'LARI (Desktop'tan adapt)
    // ========================================

    // schedule-created ve schedule-updated için ortak handler
    const handleScheduleSaveOrUpdate = (message: any) => {
      if (message.data) {
        void scheduleService.saveSchedule(message.data).finally(() => {
          this.scheduleAuthoritativePlaylistSync();
        });

        window.dispatchEvent(new CustomEvent('schedule-updated', {
          detail: message.data
        }));
      }
    };

    this.addMessageHandler('schedule-created', handleScheduleSaveOrUpdate);
    this.addMessageHandler('schedule-updated', handleScheduleSaveOrUpdate);

    // schedule-deleted mesajı handler'ı
    this.addMessageHandler('schedule-deleted', (message) => {
      const scheduleId = message.data?._id || message.data?.id || message.scheduleId;

      if (scheduleId) {
        scheduleService.deleteSchedule(scheduleId);
      }
    });

    // musicSchedule mesajı handler'ı (Yedek - eski format için)
    // Not: Backend şu an 'command' tipinde 'music-schedule' komutu gönderiyor
    // Bu handler eski format veya doğrudan musicSchedule tipi için yedek olarak duruyor
    this.addMessageHandler('musicSchedule', (message) => {
      console.log('[WebSocket] musicSchedule mesajı alındı (eski format):', message);
      const action = message.data?.action || message.action;
      const schedule = message.data?.schedule || message.schedule;

      if (action === 'set' && schedule) {
        musicScheduleService.setMusicSchedule(schedule);
      } else if (action === 'delete') {
        musicScheduleService.deleteMusicSchedule();
      }
    });

    // specialDayPlaylist mesajı handler'ı (Yedek - eski/direkt format için)
    this.addMessageHandler('specialDayPlaylist', (message) => {
      const action = message.data?.action || message.action || 'set';
      const config = message.data?.config || message.config || message.data || message;

      if (action === 'delete' || config?.enabled === false) {
        specialDayService.deleteSpecialDayConfig();
      } else {
        specialDayService.setSpecialDayConfig(config);
      }
    });
  }

  // Anons verisini normalize et - farklı formatları standart hale getir
  private normalizeAnnouncementData(announcement: any) {
    // device_announcements'tan gelen format (id field'lı)
    if (announcement.id && !announcement._id) {
      const normalized = {
        _id: announcement.id,
        title: announcement.title,
        content: announcement.content,
        startDate: announcement.startDate,
        endDate: announcement.endDate,
        scheduleType: announcement.scheduleType,
        songInterval: announcement.songInterval,
        minuteInterval: announcement.minuteInterval,
        immediateInterrupt: announcement.immediateInterrupt || false,
        specificTimes: announcement.specificTimes || [],
        activeDays: announcement.activeDays || [],
        audioFile: announcement.audioFile || [],
        audioFiles: []
      };
      
      // audioFiles oluştur
      if (announcement.audioFile && announcement.audioFile.length > 0) {
        normalized.audioFiles = announcement.audioFile.map((af: any, index: number) => ({
          id: af._id || af.id || `audio_${index}`,
          url: af.url,
          fileName: af.fileName,
          status: af.status || 'approved',
          order: index + 1
        }));
      }
      
      return normalized;
    }

    // Normal format (_id field'lı) - kopyasını oluştur
    const normalized = { ...announcement };
    normalized._id = normalized._id || normalized.id;
    normalized.activeDays = Array.isArray(normalized.activeDays) ? normalized.activeDays : [];

    // audioFiles varsa ama audioFile yoksa, audioFile oluştur
    if (normalized.audioFiles && !normalized.audioFile) {
      normalized.audioFile = normalized.audioFiles.map((af: any) => ({
        url: af.url,
        fileName: af.fileName,
        status: af.status || 'approved',
        id: af.id,
        localPath: af.localPath
      }));
    }

    // audioFile varsa ama audioFiles yoksa, audioFiles oluştur
    if (normalized.audioFile && !normalized.audioFiles) {
      normalized.audioFiles = normalized.audioFile.map((af: any) => ({
        id: af._id || af.id || `audio_${Date.now()}`,
        url: af.url,
        fileName: af.fileName,
        status: af.status || 'approved',
        localPath: af.localPath,
        order: af.order || 1
      }));
    }

    return normalized;
  }

  private getAnnouncementId(announcement: any): string | null {
    const id = announcement?._id || announcement?.id;
    return id ? String(id) : null;
  }

  private readStoredAnnouncements(): any[] {
    const parsed = localStateStore.getJson<any[]>('announcements', []);
    return Array.isArray(parsed) ? parsed : [];
  }

  private replaceStoredAnnouncements(announcements: any[]): any[] {
    const normalizedAnnouncements = (announcements || [])
      .map((announcement: any) => this.normalizeAnnouncementData(announcement))
      .filter((announcement: any) => this.getAnnouncementId(announcement));
    const deduped = new Map<string, any>();

    normalizedAnnouncements.forEach((announcement: any) => {
      const id = this.getAnnouncementId(announcement);
      if (id) {
        deduped.set(id, announcement);
      }
    });

    const result = Array.from(deduped.values());
    localStateStore.setJson('announcements', result);
    return result;
  }

  private upsertStoredAnnouncement(announcementData: any): { announcement: any; allAnnouncements: any[] } {
    const normalizedAnnouncement = this.normalizeAnnouncementData(announcementData);
    const announcementId = this.getAnnouncementId(normalizedAnnouncement);
    const existingAnnouncements = this.readStoredAnnouncements();

    if (!announcementId) {
      return {
        announcement: normalizedAnnouncement,
        allAnnouncements: existingAnnouncements
      };
    }

    const nextAnnouncements = existingAnnouncements.filter(
      (announcement: any) => this.getAnnouncementId(announcement) !== announcementId
    );
    nextAnnouncements.push(normalizedAnnouncement);
    localStateStore.setJson('announcements', nextAnnouncements);

    return {
      announcement: normalizedAnnouncement,
      allAnnouncements: nextAnnouncements
    };
  }

  private removeStoredAnnouncement(announcementId: string): any[] {
    const updatedAnnouncements = this.readStoredAnnouncements().filter(
      (announcement: any) => this.getAnnouncementId(announcement) !== String(announcementId)
    );
    localStateStore.setJson('announcements', updatedAnnouncements);
    return updatedAnnouncements;
  }

  private clearPlaylistStorage() {
    localStateStore.remove('currentPlaylist');
    localStateStore.remove('currentPlaylistSignature');
    localStateStore.remove('fallbackPlaylist');
    localStateStore.remove('fallbackPlaylistSignature');
  }

  private onmessage = (event: MessageEvent) => {
    try {
      const rawMessage = event.data;
      const message = JSON.parse(rawMessage);

      if (this.debugMessages) {
        console.log('[WebSocket] Gelen mesaj tipi:', message.type, '| Data:', JSON.stringify(message).substring(0, 200));
      }

      const handlers = this.messageHandlers.get(message.type);
      
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(message);
          } catch (error) {
            console.error('Handler hatası:', error); 
          }
        });
      } else {
      }
    } catch (error) {
      console.error('Mesaj işleme hatası:', error);
      console.error('Ham mesaj:', event.data);
    }
  };

  private bindNetworkEvents() {
    window.addEventListener('online', () => {
      this.shouldReconnect = true;
      this.reconnectTimeout = 1000;
      this.clearReconnectTimer();
      this.connect();
    });

    window.addEventListener('offline', () => {
      this.clearReconnectTimer();
    });
  }

  private createStableSignature(value: any): string {
    const normalize = (input: any): any => {
      if (Array.isArray(input)) {
        return input.map(normalize);
      }

      if (input && typeof input === 'object') {
        return Object.keys(input)
          .sort()
          .reduce((accumulator: Record<string, any>, key) => {
            accumulator[key] = normalize(input[key]);
            return accumulator;
          }, {});
      }

      return input;
    };

    const normalized = JSON.stringify(normalize(value));
    let hash = 0;

    for (let index = 0; index < normalized.length; index += 1) {
      hash = ((hash << 5) - hash + normalized.charCodeAt(index)) | 0;
    }

    return `${normalized.length}:${hash}`;
  }

  private createPlaylistSignature(playlist: any): string {
    return this.createStableSignature({
      id: playlist?._id || playlist?.id,
      name: playlist?.name,
      artwork: playlist?.artwork || playlist?.artworkUrl,
      updatedAt: playlist?.updatedAt,
      songs: (playlist?.songs || []).map((song: any) => ({
        id: song?._id || song?.id,
        title: song?.title,
        name: song?.name,
        artist: song?.artist,
        filePath: song?.filePath || song?.path,
        cdnUrl: song?.cdnUrl || song?.url,
        duration: song?.duration,
        artwork: song?.artwork
      }))
    });
  }

  private createAnnouncementsSignature(announcements: any[]): string {
    return this.createStableSignature(
      (announcements || [])
        .map((announcement: any) => ({
          id: announcement?._id || announcement?.id,
          title: announcement?.title,
          content: announcement?.content,
          scheduleType: announcement?.scheduleType,
          songInterval: announcement?.songInterval,
          minuteInterval: announcement?.minuteInterval,
          specificTimes: announcement?.specificTimes || [],
          activeDays: announcement?.activeDays || [],
          startDate: announcement?.startDate,
          endDate: announcement?.endDate,
          audio: (
            Array.isArray(announcement?.audioFile) && announcement.audioFile.length > 0
              ? announcement.audioFile
              : (announcement?.audioFiles || [])
          ).map((file: any) => ({
            id: file?._id || file?.id,
            url: file?.url,
            status: file?.status
          }))
        }))
        .sort((left: any, right: any) => String(left.id).localeCompare(String(right.id)))
    );
  }

  private storePlaylistSignature(playlist: any) {
    localStateStore.setString('currentPlaylistSignature', this.createPlaylistSignature(playlist));
  }

  private getCurrentPlaylistFromStorage(): any | null {
    return localStateStore.getJson<any>('currentPlaylist', null);
  }

  private isSchedulePlaybackActive(): boolean {
    const currentPlaylist = this.getCurrentPlaylistFromStorage();
    return Boolean(scheduleService.getActiveSchedule() || currentPlaylist?._id?.startsWith('scheduled_'));
  }

  private storeFallbackPlaylist(playlist: any) {
    localStateStore.setJson('fallbackPlaylist', playlist);
    localStateStore.setString('fallbackPlaylistSignature', this.createPlaylistSignature(playlist));
  }

  private attachDeliveryMetadata(playlist: any, message: any) {
    if (!playlist) {
      return playlist;
    }

    const assignmentId = message?.assignmentId || message?.delivery?.assignmentId || playlist?.assignmentId;
    const playlistId = message?.playlistId || message?.delivery?.playlistId || playlist?.playlistId || playlist?._id;

    if (!assignmentId && !playlistId) {
      return playlist;
    }

    return {
      ...playlist,
      __delivery: {
        assignmentId: assignmentId || null,
        playlistId: playlistId || playlist?._id || null,
        sentAt: message?.sentAt || null,
        receivedAt: Date.now()
      }
    };
  }

  private markPendingPlaylistAutoplay(playlist: any) {
    const delivery = playlist?.__delivery;
    const playlistId = delivery?.playlistId || playlist?._id;

    if (!delivery?.assignmentId || !playlistId) {
      return;
    }

    localStateStore.setJson('pendingPlaylistAutoplay', {
      assignmentId: delivery.assignmentId,
      playlistId,
      createdAt: Date.now()
    });
  }

  private getPlaylistDelivery(playlist: any) {
    return playlist?.__delivery || null;
  }

  private shouldApplyPlaylist(serverPlaylist: any): boolean {
    const serverSignature = this.createPlaylistSignature(serverPlaylist);
    const storedSignature = localStateStore.getString('currentPlaylistSignature');

    if (storedSignature) {
      return storedSignature !== serverSignature;
    }

    const localPlaylist = localStateStore.getString('currentPlaylist');
    if (!localPlaylist) {
      return true;
    }

    try {
      return this.createPlaylistSignature(JSON.parse(localPlaylist)) !== serverSignature;
    } catch {
      return true;
    }
  }

  private sendPlaylistAck(message: any, status: 'received' | 'error' = 'received', extra: Record<string, any> = {}) {
    const assignmentId = message?.assignmentId || message?.delivery?.assignmentId || message?.data?.assignmentId;
    const playlistId = message?.playlistId || message?.delivery?.playlistId || message?.data?.playlistId || message?.data?._id;

    if (!assignmentId) {
      return;
    }

    const ackKey = `${assignmentId}:${status}`;
    if (this.acknowledgedPlaylistAssignments.has(ackKey)) {
      return;
    }

    this.acknowledgedPlaylistAssignments.add(ackKey);
    this.sendMessage({
      type: 'playlistAck',
      assignmentId,
      playlistId,
      status,
      ...extra,
      timestamp: Date.now()
    });
  }

  sendPlaylistPlaybackStarted(playlist: any) {
    const delivery = this.getPlaylistDelivery(playlist);
    const playlistId = delivery?.playlistId || playlist?.playlistId || playlist?._id;

    if (!playlistId || !delivery?.assignmentId) {
      return;
    }

    const completionKey = delivery?.assignmentId
      ? `${delivery.assignmentId}:completed`
      : `${playlistId}:completed`;
    if (this.acknowledgedPlaylistAssignments.has(completionKey)) {
      return;
    }

    const totalSongs = Array.isArray(playlist?.songs) ? playlist.songs.length : 0;

    this.acknowledgedPlaylistAssignments.add(completionKey);
    try {
      const pendingRaw = localStateStore.getString('pendingPlaylistAutoplay');
      const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
      if (!pending || pending.playlistId === playlistId) {
        localStateStore.remove('pendingPlaylistAutoplay');
      }
    } catch {
      localStateStore.remove('pendingPlaylistAutoplay');
    }

    this.sendMessage({
      type: 'playlistStatus',
      status: 'completed',
      playlistId,
      playlistName: playlist?.name,
      totalSongs,
      completedSongs: totalSongs,
      progress: 100,
      source: 'web-player-streaming',
      playbackConfirmed: true,
      timestamp: Date.now()
    });
  }

  sendPlaylistPlaybackBlocked(playlist: any, reason: string) {
    const delivery = this.getPlaylistDelivery(playlist);
    const playlistId = delivery?.playlistId || playlist?.playlistId || playlist?._id;

    if (!playlistId || !delivery?.assignmentId) {
      return;
    }

    const errorKey = `${delivery.assignmentId}:${reason}`;
    if (this.acknowledgedPlaylistAssignments.has(errorKey)) {
      return;
    }

    this.acknowledgedPlaylistAssignments.add(errorKey);
    this.sendMessage({
      type: 'playlistStatus',
      status: 'error',
      playlistId,
      playlistName: playlist?.name,
      progress: 0,
      error: reason,
      source: 'web-player-streaming',
      timestamp: Date.now()
    });
  }

  private sendCommandAck(message: any, command: string, success: boolean, extra: Record<string, any> = {}) {
    const data = message?.data || {};

    this.sendMessage({
      type: 'commandAck',
      commandId: data.commandId || message?.commandId || null,
      command,
      action: data.action || message?.action || null,
      success,
      ...extra,
      timestamp: Date.now()
    });
  }

  private clearReconnectTimer() {
    if (this.reconnectTimerId) {
      window.clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  private isTransientMessage(message: any): boolean {
    return ['playbackStatus', 'pong', 'ping'].includes(message?.type);
  }

  private async resetDeletedDeviceData() {
    if (this.isDeviceResetting) {
      return;
    }

    this.isDeviceResetting = true;
    this.shouldReconnect = false;
    this.clearAuthRetryTimer();
    this.clearReconnectTimer();
    this.stopEmergencyScheduleChecker();
    this.messageQueue = [];

    try {
      this.ws?.close(4001, 'device_deleted');
    } catch {
      // ignore close errors during reset
    } finally {
      this.ws = null;
      this.isAlreadyConnected = false;
    }

    window.dispatchEvent(new CustomEvent('deviceResetting'));

    document.querySelectorAll('audio, video').forEach((media) => {
      try {
        const element = media as HTMLMediaElement;
        element.pause();
        element.removeAttribute('src');
        element.load();
      } catch {
        // media cleanup must not block device reset
      }
    });

    deviceService.clearDeviceInfo();
    localStateStore.clear();

    try {
      if (window.indexedDB?.databases) {
        const databases = await window.indexedDB.databases();
        await Promise.all(
          databases
            .filter(database => database.name)
            .map(database => window.indexedDB.deleteDatabase(database.name!))
        );
      }
    } catch {
      // IndexedDB temizlenemezse token temizligi yine de tamamlandi.
    }

    window.location.replace(window.location.origin + window.location.pathname);
  }

  connect() {
    // Zaten bağlı ise yeni bağlantı kurma
    if (this.isAlreadyConnected) {
      return;
    }

    const token = localStateStore.getString('deviceToken');
    if (!token) {
      return;
    }

    // Eğer zaten bağlıysa ve OPEN durumundaysa, yeni bağlantı kurma
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.isAlreadyConnected = true;
      return;
    }

    // Eğer bağlantısı kuruluyorsa (CONNECTING), bekle
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (this.ws) {
      this.shouldReconnect = false;
      this.ws.close();
      this.shouldReconnect = true;
    }

    this.ws = new WebSocket(WS_URL);
    
    this.isAlreadyConnected = true;

    this.ws.onopen = this.onSocketOpen;
    this.ws.onmessage = this.onmessage;
    this.ws.onclose = this.handleDisconnect;
    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  private onSocketOpen = () => {
    this.reconnectTimeout = 1000;

    // Bağlantısı kurulduğunda token bilgisini gönder
    const deviceToken = localStateStore.getString('deviceToken');
    if (deviceToken) {
      // Tarayıcı bilgisini tespit et
      const browserInfo = this.getBrowserInfo();

      this.sendMessage({
        type: 'auth',
        token: deviceToken,
        deviceId: deviceToken,
        browserInfo: browserInfo
      });
      
      localStateStore.setString('deviceId', deviceToken);
      
      // Token doğrulama başarısız olursa yeniden deneme mekanizmasını başlat
      this.startAuthRetryTimer();
    } else {
    }
    
    // Bekleyen mesajları gönder
    this.processMessageQueue();
  };

  private handleDisconnect = () => {
    this.isAlreadyConnected = false;
    
    if (!navigator.onLine) {
      this.clearReconnectTimer();
      return;
    }
    
    const nextDelay = Math.min(this.reconnectTimeout * 2, this.maxReconnectTimeout);
    this.reconnectTimeout = nextDelay;
    
    if (this.shouldReconnect) {
      this.clearReconnectTimer();
      this.reconnectTimerId = window.setTimeout(() => {
        this.connect();
      }, this.reconnectTimeout);
    }
  };

  sendMessage(message: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const messageStr = JSON.stringify(message);
      this.ws.send(messageStr);
    } else {
      if (this.isTransientMessage(message)) {
        return;
      }

      if (this.messageQueue.length >= this.maxQueuedMessages) {
        this.messageQueue.shift();
      }

      this.messageQueue.push(message);
      // Yeniden bağlanmayı dene
      if (this.ws?.readyState === WebSocket.CLOSED) {
        this.connect();
      }
    }
  }

  sendAuth(token: string) {
    
    // Bağlantısı yoksa veya kapalıysa önce bağlantıyı kur
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connect();
      
      // Bağlantısı kurulduktan sonra auth gönder
      this.ws!.addEventListener('open', () => {
        this.sendMessage({ type: 'auth', token, deviceId: token });
      }, { once: true });
    } else {
      // Bağlantısı varsa direkt gönder
      this.sendMessage({ type: 'auth', token, deviceId: token });
    }
  }

  private scheduleAuthoritativePlaylistSync() {
    if (this.activePlaylistResyncTimerId) {
      window.clearTimeout(this.activePlaylistResyncTimerId);
    }

    this.activePlaylistResyncTimerId = window.setTimeout(() => {
      this.activePlaylistResyncTimerId = null;
      this.requestActivePlaylist(true);
    }, 250);
  }

  requestActivePlaylist(forceSync: boolean = false) {
    const token = localStateStore.getString('deviceToken');
    
    if (!token) {
      console.error('Token bulunamadı, aktif playlist sorgulanamıyor');
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket bağlantısı yok, aktif playlist sorgulanamıyor');
      return;
    }

    this.sendMessage({
      type: 'getActivePlaylist',
      token: token,
      forceSync
    });
  }

  requestUiSettings() {
    const token = localStateStore.getString('deviceToken');

    if (!token) {
      console.error('Token bulunamadı, UI ayarları sorgulanamıyor');
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket bağlantısı yok, UI ayarları sorgulanamıyor');
      return;
    }

    this.sendMessage({
      type: 'uiSettings',
      token
    });
  }

  requestDeviceAnnouncements() {
    const token = localStateStore.getString('deviceToken');
    
    if (!token) {
      console.error('Token bulunamadı, anonslar sorgulanamıyor');
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket bağlantısı yok, anonslar sorgulanamıyor');
      return;
    }

    this.sendMessage({
      type: 'announcement',
      action: 'check_device_announcements',
      data: {
        deviceId: token,
        checkType: 'device_announcements'
      }
    });
  }

  requestPrayerTimes() {
    const token = localStateStore.getString('deviceToken');
    
    if (!token) {
      console.error('Token bulunamadı, ezan vakitleri sorgulanamıyor');
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket bağlantısı yok, ezan vakitleri sorgulanamıyor');
      return;
    }

    
    // Desktop'taki gibi deneyelim - getPrayerTimes
    this.sendMessage({
      type: 'getPrayerTimes',
      token: token
    });
    
    // Alternatif: getAllPrayerTimes
    this.sendMessage({
      type: 'getAllPrayerTimes',
      token: token
    });
  }

  getConnectionState() {
    if (!this.ws) return 'CLOSED';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'OPEN';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }

  addMessageHandler(type: string, handler: MessageHandler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    this.messageHandlers.get(type)?.add(handler);
  }

  removeMessageHandler(type: string, handler: MessageHandler) {
    this.messageHandlers.get(type)?.delete(handler);
  }

  disconnect() {
    this.shouldReconnect = false;
    this.ws?.close();
    this.isAlreadyConnected = false;
    this.clearAuthRetryTimer();
    if (this.activePlaylistResyncTimerId) {
      window.clearTimeout(this.activePlaylistResyncTimerId);
      this.activePlaylistResyncTimerId = null;
    }
  }
    
  private startAuthRetryTimer() {
    // Önceki zamanlayıcıyı temizle
    this.clearAuthRetryTimer();
    
    // Her 5 saniyede bir token doğrulamayı yeniden dene
    this.authRetryTimerId = window.setInterval(() => {
    const deviceToken = localStateStore.getString('deviceToken');
      
      if (deviceToken) {
        
        // WebSocket bağlantısı yoksa önce bağlan
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          this.connect();
        }
        
        // Token doğrulamayı dene
        this.sendAuth(deviceToken);
      } else {
        this.clearAuthRetryTimer();
      }
    }, 5000);
  }
  
  // Auth yeniden deneme zamanlayıcısını temizle
  private clearAuthRetryTimer() {
    if (this.authRetryTimerId) {
      clearInterval(this.authRetryTimerId);
      this.authRetryTimerId = null;
    }
  }

  private processMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }

  // Emergency schedule timer - 30 saniyede bir kontrol
  private startEmergencyScheduleChecker() {
    this.stopEmergencyScheduleChecker();

    this.emergencyScheduleTimerId = window.setInterval(() => {
      this.checkEmergencySchedule();
    }, 30 * 1000);

    // İlk kontrolü hemen yap
    this.checkEmergencySchedule();
    console.log('[WebSocket] Emergency schedule timer başlatıldı (30s)');
  }

  private stopEmergencyScheduleChecker() {
    if (this.emergencyScheduleTimerId) {
      clearInterval(this.emergencyScheduleTimerId);
      this.emergencyScheduleTimerId = null;
    }
  }

  private checkEmergencySchedule() {
    const raw = localStateStore.getString('emergencySchedule');
    if (!raw) return;

    try {
      const schedule = JSON.parse(raw);
      if (!schedule?.enabled) return;

      const now = new Date();
      const startDate = new Date(schedule.startDate);
      const endDate = new Date(schedule.endDate);

      console.log(`[WebSocket] Schedule kontrol - now: ${now.toISOString()}, start: ${startDate.toISOString()}, end: ${endDate.toISOString()}`);

      // Bitiş zamanı geçmiş → acil durumu kaldır, schedule temizle
      if (now >= endDate) {
        const isActive = localStateStore.getString('emergencyStopped') === 'true';
        if (isActive) {
          console.log('[WebSocket] Schedule bitiş zamanı geldi, acil durum kaldırılıyor');
          localStateStore.remove('emergencyStopped');
          localStateStore.remove('emergencyReason');
          window.dispatchEvent(new CustomEvent('emergencyReset'));
        }
        localStateStore.remove('emergencySchedule');
        this.stopEmergencyScheduleChecker();
        return;
      }

      // Başlangıç zamanı gelmiş ve henüz aktif değil → durdur
      if (now >= startDate && now < endDate) {
        const isActive = localStateStore.getString('emergencyStopped') === 'true';
        if (!isActive) {
          const reason = schedule.reason || 'Zamanlı acil durdur aktif';
          console.log('[WebSocket] Schedule başlangıç zamanı geldi, acil durum aktifleştiriliyor');
          localStateStore.setString('emergencyStopped', 'true');
          localStateStore.setString('emergencyReason', reason);
          window.dispatchEvent(new CustomEvent('emergencyStop', { detail: { reason } }));
        }
      }
    } catch (e) {
      console.error('[WebSocket] Schedule parse hatası:', e);
    }
  }

  // Tarayıcı bilgisini tespit et
  private getBrowserInfo(): { name: string; version: string } {
    const userAgent = navigator.userAgent;
    let browserName = 'unknown';
    let browserVersion = 'unknown';

    // ÖNEMLI: Sıralama önemli! Chromium tabanlı tarayıcılar UserAgent'da "Chrome" içerir
    // Bu yüzden özel tarayıcıları (Edge, Opera, Brave) önce kontrol etmeliyiz

    // Brave özel kontrol - UserAgent'ta "Brave" yazmaz, navigator.brave API kullanılır
    if ((navigator as any).brave && typeof (navigator as any).brave.isBrave === 'function') {
      browserName = 'Brave';
      const match = userAgent.match(/chrome\/(\d+)/i);
      if (match) browserVersion = match[1];
    } else if (userAgent.match(/edg/i)) {
      browserName = 'Edge';
      const match = userAgent.match(/edg[a-z]*\/(\d+)/i);
      if (match) browserVersion = match[1];
    } else if (userAgent.match(/opr\//i) || userAgent.match(/opera/i)) {
      browserName = 'Opera';
      const match = userAgent.match(/opr\/(\d+)/i);
      if (match) browserVersion = match[1];
    } else if (userAgent.match(/firefox|fxios/i)) {
      browserName = 'Firefox';
      const match = userAgent.match(/firefox\/(\d+)/i);
      if (match) browserVersion = match[1];
    } else if (userAgent.match(/safari/i) && !userAgent.match(/chrome/i)) {
      browserName = 'Safari';
      const match = userAgent.match(/version\/(\d+)/i);
      if (match) browserVersion = match[1];
    } else if (userAgent.match(/chrome|chromium|crios/i)) {
      browserName = 'Chrome';
      const match = userAgent.match(/chrome\/(\d+)/i);
      if (match) browserVersion = match[1];
    }

    return { name: browserName, version: browserVersion };
  }

  // getInstance metodu
  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }
}

// WebSocketService sınıfını ve instance'ı export et
export { WebSocketService };
export default WebSocketService.getInstance();
